import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agency } from '../types/Agency';
import { agencyApi, AgencyApiError } from '../utils/agencyApi';
import { clearUserSession, extractUserAccessToken, extractUserRefreshToken, setUserTokens } from '../utils/userAuth';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

interface AgencyAuthContextType {
  agency: Agency | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; requiresPasswordChange?: boolean }>;
  logout: () => void;
  updateAgency: (updatedAgency: Agency) => void;
  refreshAgencyDetails: () => Promise<void>;
  user: any;
}

const AgencyAuthContext = createContext<AgencyAuthContextType | undefined>(undefined);

export const useAgencyAuth = () => {
  const context = useContext(AgencyAuthContext);
  if (!context) {
    throw new Error('useAgencyAuth must be used within an AgencyAuthProvider');
  }
  return context;
};

interface AgencyAuthProviderProps {
  children: ReactNode;
}

export const AgencyAuthProvider: React.FC<AgencyAuthProviderProps> = ({ children }) => {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const getStoredUserEmail = (): string | undefined => {
    try {
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        const parsedUser = JSON.parse(storedUserInfo) as { email?: string };
        if (parsedUser?.email) {
          return parsedUser.email;
        }
      }

      const storedAgencyInfo = localStorage.getItem('agencyInfo');
      if (storedAgencyInfo) {
        const parsedAgency = JSON.parse(storedAgencyInfo) as { email?: string };
        if (parsedAgency?.email) {
          return parsedAgency.email;
        }
      }
    } catch (error) {
      // Ignore malformed storage values.
    }

    return undefined;
  };

  const normalizeAgencyData = (
    agencyPayload: unknown,
    fallbackEmail?: string,
    requiresPasswordChange?: boolean
  ): Agency | null => {
    if (!agencyPayload || typeof agencyPayload !== 'object') {
      return null;
    }

    const normalizedAgency = agencyPayload as Partial<Agency>;
    if (normalizedAgency.agency_no === undefined || normalizedAgency.agency_no === null) {
      return null;
    }

    return {
      ...normalizedAgency,
      email: normalizedAgency.email || fallbackEmail || getStoredUserEmail(),
      requires_password_change:
        normalizedAgency.requires_password_change ?? requiresPasswordChange ?? false,
    } as Agency;
  };

  const handleForcedLogout = useCallback(() => {
    clearUserSession();
    setToken(null);
    setAgency(null);
    navigate('/user/login');
  }, [navigate]);

  const fetchAgencyDetails = async (): Promise<Agency | null> => {
    try {
      const data = await agencyApi.get('/profile');

      const agencyData = (data as { agency?: unknown }).agency || data;
      const profileUserEmail = (data as { user?: { email?: string } })?.user?.email;

      const normalizedAgency = normalizeAgencyData(agencyData, profileUserEmail);
      if (normalizedAgency) {
        return normalizedAgency;
      }
    } catch (error) {
      if (error instanceof AgencyApiError && error.isTokenExpired) {
        handleForcedLogout();
      }
      return null;
    }

    return null;
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('agencyToken') || localStorage.getItem('userToken');

      if (storedToken) {
        setToken(storedToken);
        // Always clear stale cached role data and fetch fresh details.
        localStorage.removeItem('agencyInfo');

        // Fetch agency details if token exists but no cached agency data
        const freshAgencyData = await fetchAgencyDetails();
        if (freshAgencyData) {
          setAgency(freshAgencyData);
          localStorage.setItem('agencyInfo', JSON.stringify(freshAgencyData));
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${VITE_API_BASE_URL}/user/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const responseText = await response.text();

      if (response.ok) {
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          return { success: false, error: 'Server returned invalid response format.' };
        }

        const accessToken = extractUserAccessToken(data);
        const refreshToken = extractUserRefreshToken(data);

        // Store the token
        if (accessToken && data.user?.role?.name?.toLowerCase() === 'agency') {
          const requiresPasswordChange =
            data.requires_password_change ??
            data.requiresPasswordChange ??
            data.user?.requires_password_change ??
            data.user?.require_password_change ??
            data.user?.force_password_change ??
            data.user?.must_change_password ??
            data.agency?.requires_password_change ??
            data.agency?.require_password_change ??
            data.agency?.force_password_change ??
            data.agency?.must_change_password ??
            false;

          localStorage.setItem('agencyToken', accessToken);
          setUserTokens(accessToken, refreshToken);

          // Validate and store agency data
          const agencyData = normalizeAgencyData(data.agency, data.user?.email, requiresPasswordChange);
          if (!agencyData) {
            return { success: false, error: 'Agency account is missing agency_no. Please contact support.' };
          }

          localStorage.setItem('agencyInfo', JSON.stringify(agencyData));

          setToken(accessToken);
          setAgency(agencyData);

          return {
            success: true,
            requiresPasswordChange
          };
        } else {
          return { success: false, error: 'Invalid role or missing agency data' };
        }
      } else {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
          return { success: false, error: errorData.message || 'Invalid credentials' };
        } catch (parseError) {
          if (responseText.includes('<!doctype') || responseText.includes('<html')) {
            return { success: false, error: `Server error (${response.status}). Please check if the API is running.` };
          } else {
            return { success: false, error: `Server error: ${response.status} - ${response.statusText}` };
          }
        }
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return { success: false, error: 'Cannot connect to server. Please check if the backend is running.' };
      } else {
        return { success: false, error: 'Connection error. Please try again.' };
      }
    }
  };

  const logout = useCallback(() => {
    handleForcedLogout();
  }, [handleForcedLogout]);

  const updateAgency = (updatedAgency: Agency) => {
    setAgency(updatedAgency);
    localStorage.setItem('agencyInfo', JSON.stringify(updatedAgency));
  };

  const isAuthenticated = !!token;

  const refreshAgencyDetails = async () => {
    if (!token) {
      return;
    }

    const freshAgencyData = await fetchAgencyDetails();

    if (freshAgencyData) {
      setAgency(freshAgencyData);
      localStorage.setItem('agencyInfo', JSON.stringify(freshAgencyData));
    } else {
      handleForcedLogout();
    }
  };

  return (
    <AgencyAuthContext.Provider
      value={{
        agency,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateAgency,
        refreshAgencyDetails,
        user: null, // Add user property here if needed
      }}
    >
      {children}
    </AgencyAuthContext.Provider>
  );
};